import { useState } from 'react';
import { BarChart3, Cloud, CloudOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import ForecastGenerationSummaryWidget from './ForecastGenerationSummaryWidget';
import DailyHourlyForecastWidget from './DailyHourlyForecastWidget';

const FORECAST_TYPES = [
  { key: 'day_ahead', labelKey: 'af_type_day_ahead' },
  { key: 'intraday', labelKey: 'af_type_intraday' },
  { key: 'real_time', labelKey: 'af_type_real_time' },
  { key: 'monthly', labelKey: 'af_type_monthly' },
];

interface ForecastingConfig {
  id: string;
  forecastProvider: string;
  forecastType: string;
  updateFrequency: string;
  forecastHorizon: string;
  responsibleParty: string;
}

interface Props {
  assetId: string;
  configs?: ForecastingConfig[];
  onSaved: () => void;
}

type ForecastTab = 'generation' | 'configs';

export default function TabForecasting({ assetId, configs = [], onSaved }: Props) {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(configs.length === 0);
  const [activeForecastTab, setActiveForecastTab] = useState<ForecastTab>('generation');
  const [form, setForm] = useState({
    forecastProvider: '',
    forecastType: '',
    updateFrequency: '',
    forecastHorizon: '',
    responsibleParty: '',
  });

  const handleAdd = async () => {
    if (!form.forecastProvider || !form.forecastType) {
      addToast(t('af_required'), 'warning');
      return;
    }
    setSaving(true);
    setApiError(null);
    try {
      const payload: Record<string, unknown> = {
        forecastProvider: form.forecastProvider,
        forecastType: form.forecastType,
      };
      if (form.updateFrequency) payload.updateFrequency = form.updateFrequency;
      if (form.forecastHorizon) payload.forecastHorizon = form.forecastHorizon;
      if (form.responsibleParty) payload.responsibleParty = form.responsibleParty;

      await api.post(`/assets/${assetId}/forecasting-configs`, payload);
      addToast(t('af_added_ok'), 'success');
      setForm({ forecastProvider: '', forecastType: '', updateFrequency: '', forecastHorizon: '', responsibleParty: '' });
      setShowForm(false);
      onSaved();
    } catch (err) {
      const message = getApiErrorMessage(err, t('af_add_failed'));
      setApiError(message);
      addToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const textMain = 'var(--text)';
  const textMuted = 'var(--text-muted)';
  const inputBg = 'var(--surface-solid)';
  const inputBorder = 'var(--line-strong)';
  const inputClass = 'w-full px-3 py-2 text-sm rounded-md outline-none transition-colors';
  const inputStyle = {
    backgroundColor: inputBg,
    border: `1px solid ${inputBorder}`,
    color: textMain,
  };
  const sectionBg = 'var(--surface-2)';
  const sectionBorder = 'var(--line)';
  const cardBg = 'var(--surface-solid)';

  const FORECAST_TYPE_LABELS: Record<string, string> = {
    day_ahead: t('af_type_day_ahead'),
    intraday: t('af_type_intraday'),
    real_time: t('af_type_real_time'),
    monthly: t('af_type_monthly'),
  };

  const SUB_TABS: { key: ForecastTab; label: string; icon: string }[] = [
    { key: 'generation', label: t('af_generation'), icon: 'ri-bar-chart-box-line' },
    { key: 'configs', label: t('af_tab_configs'), icon: 'ri-settings-3-line' },
  ];

  return (
    <div>
      {/* Sub-tabs */}
      <div className="flex items-center gap-0 mb-5" style={{ borderBottom: '1px solid var(--line)' }}>
        {SUB_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveForecastTab(tab.key)}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium cursor-pointer transition-colors whitespace-nowrap relative"
            style={{
              color: activeForecastTab === tab.key ? 'var(--text)' : 'var(--text-muted)',
              borderBottom: activeForecastTab === tab.key ? '2px solid var(--text)' : '2px solid transparent',
              marginBottom: '-1px',
            }}
          >
            <i className={`${tab.icon} text-sm`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Forecast Generation tab ── */}
      {activeForecastTab === 'generation' && (
        <div className="space-y-5">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--surface-2)' }}>
              <BarChart3 size={15} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
            </div>
            <div>
              <h3 className="text-sm font-semibold" style={{ color: textMain }}>{t('af_generation')}</h3>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Latest ML-based generation forecast for this asset</p>
            </div>
          </div>
          <ForecastGenerationSummaryWidget assetId={assetId} />
          <DailyHourlyForecastWidget assetId={assetId} showActualLine />
        </div>
      )}

      {/* ── Configurations tab ── */}
      {activeForecastTab === 'configs' && (
        <div>
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--surface-2)' }}>
                <Cloud size={15} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
              </div>
              <div>
                <h3 className="text-sm font-semibold" style={{ color: textMain }}>{t('af_title')}</h3>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('af_sub')}</p>
              </div>
            </div>
            {!showForm && (
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
                style={{ border: '1px solid var(--accent)', color: 'var(--accent)' }}
              >
                <i className="ri-add-line"></i> Add Config
              </button>
            )}
          </div>

          {/* Existing configs */}
          {configs.length > 0 && (
            <div className="space-y-2 mb-5">
              {configs.map((fc) => (
                <div key={fc.id} className="p-4 rounded-lg" style={{ border: `1px solid ${sectionBorder}`, background: cardBg }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Cloud size={15} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
                      <span className="text-sm font-medium" style={{ color: textMain }}>{fc.forecastProvider}</span>
                    </div>
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: 'rgba(31,91,143,0.1)', color: 'var(--series-3)' }}>
                      {FORECAST_TYPE_LABELS[fc.forecastType] ?? fc.forecastType}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--text-muted)' }}>
                    {fc.updateFrequency && <span><i className="ri-time-line mr-1"></i>{fc.updateFrequency}</span>}
                    {fc.forecastHorizon && <span><i className="ri-calendar-line mr-1"></i>Horizon: {fc.forecastHorizon}</span>}
                    {fc.responsibleParty && <span><i className="ri-user-line mr-1"></i>{fc.responsibleParty}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add form */}
          {showForm && (
            <div className="p-5 rounded-xl" style={{ background: sectionBg, border: `1px solid ${sectionBorder}` }}>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold" style={{ color: textMain }}>{t('af_new')}</h4>
                {configs.length > 0 && (
                  <button onClick={() => setShowForm(false)} className="text-xs cursor-pointer" style={{ color: 'var(--text-muted)' }}>
                    {t('common_cancel')}
                  </button>
                )}
              </div>

              {apiError && (
                <div className="mb-4 px-4 py-3 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}>
                  <i className="ri-error-warning-line mr-2"></i>{apiError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
                    {t('af_provider')} <span style={{ color: 'var(--text-dim)' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={form.forecastProvider}
                    onChange={(e) => setForm((prev) => ({ ...prev, forecastProvider: e.target.value }))}
                    placeholder={`${t('common_eg')} Helios ML Engine, Solargis`}
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
                    {t('af_type')} <span style={{ color: 'var(--text-dim)' }}>*</span>
                  </label>
                  <select
                    value={form.forecastType}
                    onChange={(e) => setForm((prev) => ({ ...prev, forecastType: e.target.value }))}
                    className={inputClass}
                    style={inputStyle}
                  >
                    <option value="">{t('common_select_type')}</option>
                    {FORECAST_TYPES.map((ft) => (
                      <option key={ft.key} value={ft.key}>{t(ft.labelKey)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>{t('af_frequency')}</label>
                  <input
                    type="text"
                    value={form.updateFrequency}
                    onChange={(e) => setForm((prev) => ({ ...prev, updateFrequency: e.target.value }))}
                    placeholder={`${t('common_eg')} Every 15min, Hourly, Daily`}
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>{t('af_horizon')}</label>
                  <input
                    type="text"
                    value={form.forecastHorizon}
                    onChange={(e) => setForm((prev) => ({ ...prev, forecastHorizon: e.target.value }))}
                    placeholder={`${t('common_eg')} 24h, 48h, 7 days`}
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>{t('af_responsible')}</label>
                  <input
                    type="text"
                    value={form.responsibleParty}
                    onChange={(e) => setForm((prev) => ({ ...prev, responsibleParty: e.target.value }))}
                    placeholder={`${t('common_eg')} Helios Aggregate LLC`}
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={handleAdd}
                  disabled={saving || !form.forecastProvider || !form.forecastType}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
                  style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}
                >
                  {saving ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Adding...</>
                  ) : (
                    <><i className="ri-add-line"></i> Add Config</>
                  )}
                </button>
              </div>
            </div>
          )}

          {configs.length === 0 && !showForm && (
            <div className="text-center py-8">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: sectionBg }}>
                <CloudOff size={20} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
              </div>
              <p className="text-sm font-medium" style={{ color: textMain }}>{t('af_empty')}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{t('af_empty_hint')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
